import React, { useCallback, useMemo, useState } from 'react'

import { createColumnHelper } from '@tanstack/react-table'
import { Card } from 'antd'
import { useTableState } from 'govwell-ui/components/Table/use-table-state'

import EmptyState from 'src/components/EmptyState'
import DashboardLabel from 'src/components/Visualizations/DashboardLabel'
import { Input, Flex, Table, T } from 'src/govwell-ui'

type TopicRow = {
  id: string
  label: string
  rank: number
  value: number
}

type Props = {
  isLoading: boolean
  topics: { id: string; label: string; value: number }[]
}

const TopicFrequencyTable = ({ isLoading, topics }: Props) => {
  const [filterText, setFilterText] = useState('')

  const handleFilterChange = useCallback((value: string) => {
    setFilterText(value)
  }, [])

  const getRowId = useCallback((row: TopicRow) => row.id, [])

  const tableData: TopicRow[] = useMemo(() => {
    return topics
      .map((topic, index) => ({
        id: topic.id,
        label: topic.label,
        rank: index + 1,
        value: topic.value,
      }))
      .filter((row) => {
        if (!filterText) {
          return true
        }
        const searchLower = filterText.toLowerCase()
        return (
          row.label.toLowerCase().includes(searchLower) ||
          row.value.toString().includes(searchLower)
        )
      })
  }, [topics, filterText])

  const columnHelper = useMemo(() => createColumnHelper<TopicRow>(), [])

  const columns = useMemo(
    () => [
      columnHelper.accessor('rank', {
        header: '#',
        cell: (info) => <T.Body className="text-gray-500">{info.getValue()}</T.Body>,
        enableSorting: true,
        sortingFn: 'auto',
      }),
      columnHelper.accessor('label', {
        header: 'Topic',
        cell: (info) => <T.Body>{info.getValue()}</T.Body>,
        enableSorting: true,
        sortingFn: 'auto',
      }),
      columnHelper.accessor((row) => row.value, {
        id: 'value',
        header: '# Chats',
        cell: (info) => <T.Body>{info.getValue()}</T.Body>,
        enableSorting: true,
        sortingFn: 'auto',
      }),
    ],
    [columnHelper]
  )

  const isEmpty = !isLoading && topics.length === 0

  const tableProps = useTableState<TopicRow>()

  if (isLoading) {
    return null
  }

  return (
    <Card className="w-full flex-shrink flex-grow">
      <Flex direction="column" gap={4}>
        <Flex justify="space-between" align="center">
          <DashboardLabel label="Topic Frequency" isBold />
          {!isEmpty && (
            <Input
              ariaLabel="Filter topics"
              placeholder="Filter topics..."
              value={filterText}
              onValueChange={handleFilterChange}
            />
          )}
        </Flex>
        {isEmpty ? (
          <EmptyState hideBorder>
            <EmptyState.Image />
            <EmptyState.Message>No topics recorded for this range.</EmptyState.Message>
          </EmptyState>
        ) : (
          <Table
            columns={columns}
            data={tableData}
            getRowId={getRowId}
            isSortedOnClient
            totalCount={tableData.length}
            {...tableProps}
          />
        )}
      </Flex>
    </Card>
  )
}

export default React.memo(TopicFrequencyTable)
